import { useLang } from "@/context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { Reveal, Overline } from "@/components/Reveal";
import { Shield, BookOpen, Tablet, HeartHandshake, GraduationCap, Sparkles, ArrowRight, Sparkle } from "lucide-react";

const featureIcons = { shield: Shield, book: BookOpen, tablet: Tablet };
const benefitIcons = { parents: HeartHandshake, teachers: GraduationCap, students: Sparkles };

const scrollTo = (id) => () => {
  const el = document.getElementById(id);
  if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
};

export default function WhyICDH() {
  const { t } = useLang();
  const navigate = useNavigate();
  const w = t.why;

  return (
    <section id="why" data-testid="why-section" className="relative bg-icdh-white py-28 md:py-40 overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-icdh-red/5 blur-3xl" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-3xl">
          <Reveal><Overline>{w.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-icdh-navy">{w.title}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-icdh-navy/70 text-lg">{w.intro}</p>
          </Reveal>
        </div>

        {/* Feature cards */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {w.features.map((f, i) => {
            const Icon = featureIcons[f.icon] || Shield;
            return (
              <Reveal key={i} delay={i * 0.12}>
                <div data-testid={`why-feature-${f.icon}`} className="group h-full border border-icdh-navy/12 bg-icdh-surface/40 p-8 hover:border-icdh-red hover:-translate-y-1 transition-[transform,border-color] duration-500">
                  <div className="h-14 w-14 flex items-center justify-center bg-icdh-navy text-icdh-white group-hover:bg-icdh-red transition-colors duration-500">
                    <Icon size={26} strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-7 font-sans font-bold text-xl tracking-tight text-icdh-navy">{f.t}</h3>
                  <p className="mt-3 text-icdh-navy/65 text-sm leading-relaxed">{f.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Benefits */}
        <div className="mt-24">
          <Reveal>
            <h3 className="font-serif text-3xl md:text-4xl tracking-tight text-icdh-navy">{w.benefits_title}</h3>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-px bg-icdh-navy/10 border border-icdh-navy/10">
            {w.benefits.map((b, i) => {
              const Icon = benefitIcons[b.icon] || Sparkles;
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div data-testid={`why-benefit-${b.icon}`} className="bg-icdh-white p-8 h-full">
                    <div className="flex items-center gap-3 text-icdh-red">
                      <Icon size={22} strokeWidth={1.6} />
                      <span className="font-serif text-4xl text-icdh-navy/15">0{i + 1}</span>
                    </div>
                    <h4 className="mt-5 font-sans font-bold text-lg text-icdh-navy">{b.t}</h4>
                    <p className="mt-2 text-icdh-navy/65 text-sm leading-relaxed">{b.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Closing + CTAs */}
        <Reveal delay={0.1}>
          <div className="mt-20 relative overflow-hidden bg-icdh-navy text-icdh-white p-10 md:p-16">
            <div className="noise-overlay absolute inset-0 opacity-[0.05] pointer-events-none" />
            <div className="relative">
              <p className="max-w-3xl font-serif italic text-2xl md:text-3xl leading-snug">{w.closing}</p>

              <div className="mt-10 inline-flex items-center gap-3 bg-emerald-500 text-white px-6 py-3 font-bold tracking-[0.12em] text-sm md:text-base uppercase shadow-lg" data-testid="why-free-badge">
                <Sparkle size={20} className="animate-pulse" />
                {w.free_badge}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <button onClick={() => navigate("/stem")} data-testid="why-cta-stem"
                  className="group inline-flex items-center justify-center gap-3 h-14 px-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs md:text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300">
                  {w.cta_stem}
                  <BookOpen size={18} className="group-hover:scale-110 transition-transform" />
                </button>
                <button onClick={scrollTo("admissions")} data-testid="why-cta-enroll"
                  className="group inline-flex items-center justify-center gap-3 h-14 px-8 bg-amber-500 hover:bg-amber-600 text-icdh-navy text-xs md:text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300">
                  {w.cta_enroll}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
